import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import { NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import React from "react";
import Typography from "@material-ui/core/Typography";
import { capitalize } from "../../helpers";
import classNames from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import useMountEffect from "../../mountEffect";

const useStyles = makeStyles((theme) => ({
  badge: {
    width: "20px",
    height: "20px",
    display: "flex",
    zIndex: 1,
    flexWrap: "wrap",
    fontSize: "0.75rem",
    alignItems: "center",
    borderRadius: "50%",
    alignContent: "center",
    flexDirection: "row",
    justifyContent: "center",
  },
  menuLink: {
    position: "relative",
    display: "block",
  },
  menuItem: {
    paddingLeft: "15px",
    paddingRight: 0,
    paddingTop: theme.spacing(1) * 1.5,
    paddingBottom: theme.spacing(1) * 1.5,
  },
  menuIcon: {
    marginLeft: theme.spacing(1) * 2,
    marginRight: theme.spacing(1) * 2,
  },
  menuSubItem: {
    paddingLeft: "0",
    paddingRight: "55px",
    paddingTop: theme.spacing(1) * 1.5,
    paddingBottom: theme.spacing(1) * 1.5,
  },
  menuSubSubItem: {
    paddingLeft: "0",
    paddingRight: "0",
  },
  menuSubItemPadding: {
    paddingLeft: "55px",
    paddingRight: "55px",
  },
  menuCollapsed: {
    backgroundColor: theme.palette.action.hover,
    '&:first-of-type':{
      borderBottom: "1px solid rgba(0, 0, 0, 0.12)", 
    }
  },
  menuActive: {
    backgroundColor: theme.palette.action.hover,
  },
  menuClosed: {
    backgroundColor: "transparent",
  },
  caret: {
    marginLeft: theme.spacing(1) * 2,
    marginRight: theme.spacing(1) * 2,
    minWidth: 0,
  },
  primary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  secondary: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  error: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  title: {
    fontWeight: 800,
  },
}));

const SidebarItem = ({
  route,
  index,
  activeRoute,
  toggleMenu,
  currentPath,
  activeSubRoute,
  toggleSubMenu,
}) => {
  const classes = useStyles();

  useMountEffect(() => {
    if (!currentPath || activeRoute === index || route.path === "/") return;
    toggleMenu(index);
  });

  const badge = (badge) => {
    if (!badge) return;
    const badgeClassName = classNames(classes.badge, {
      [classes[`${badge.type}`]]: badge.type !== "default",
    });
    return (
      <Typography
        className={classNames(classes.badge, badgeClassName)}
        component="div"
      >
        {badge.value}
      </Typography>
    );
  };

  if (route.type === "external") {
    return (
      <a
        href={route.path}
        target="_blank"
        rel="noopener noreferrer"
        key={index}
        className={classes.menuLink}
      >
        <ListItem className={classes.menuItem} button>
          <ListItemIcon>
            <route.icon className={classes.menuIcon} />
          </ListItemIcon>
          <Typography variant="body1" className="flexSpacer">
            {capitalize(route.name)}
          </Typography>
          {badge(route.badge)}
        </ListItem>
      </a>
    );
  }

  if (route.type === "submenu") {
    return (
      <div
        className={
          activeRoute === index || activeRoute !== 2
            ? classes.menuCollapsed
            : classes.menuClosed
        }
      >
        <ListItem
          className={classes.menuItem}
          button
          key={index}
          onClick={() => toggleMenu(index)}
        >
          <Typography variant="body1" className="flexSpacer">
            <span className={classes.title}>{capitalize(route.name)}</span>
          </Typography>
          {badge(route.badge)}
          <ListItemIcon className={classes.caret}>
            {activeRoute === index ? (
              <ArrowDropUpIcon />
            ) : (
              <ArrowDropDownIcon />
            )}
          </ListItemIcon>
        </ListItem>
        <Collapse
          in={activeRoute === index ? true : false}
          timeout="auto"
          unmountOnExit
        >
          <List>
            {route.children.map((subitem, index) =>
              subitem.type === "external" ? (
                <a
                  href={subitem.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={index}
                  className={classes.menuLink}
                >
                  <ListItem className={classes.menuSubItem} button>
                    <ListItemIcon>
                      <subitem.icon className={classes.menuIcon} />
                    </ListItemIcon>
                    <Typography variant="body1" className="flexSpacer">
                      {capitalize(subitem.name)}
                    </Typography>
                    {badge(route.badge)}
                  </ListItem>
                </a>
              ) : subitem.type === "submenu" ? (
                <div
                  key={index}
                  className={
                    activeRoute === index
                      ? classes.menuCollapsed
                      : classes.menuClosed
                  }
                >
                  <ListItem
                    className={classes.menuSubSubItem}
                    button
                    key={index}
                    onClick={() => toggleSubMenu(index)}
                  >
                    <ListItemIcon>
                      <subitem.icon className={classes.menuIcon} />
                    </ListItemIcon>
                    <Typography variant="body1" className="flexSpacer">
                      {capitalize(subitem.name)}
                    </Typography>
                    {badge(subitem.badge)}
                    <ListItemIcon className={classes.caret}>
                      {activeSubRoute === index ? (
                        <ArrowDropUpIcon />
                      ) : (
                        <ArrowDropDownIcon />
                      )}
                    </ListItemIcon>
                  </ListItem>
                  <Collapse
                    in={activeSubRoute === index ? true : false}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List>
                      {subitem.children.map((subChild, idx) => (
                        <NavLink
                          to={`${
                            subitem.path ? `${route.path}${subitem.path}` : ""
                          }${subChild.path ? subChild.path : ""}`}
                          exact
                          className={classes.menuLink}
                          activeClassName={classes.menuActive}
                          key={idx}
                        >
                          <ListItem
                            className={classes.menuSubItemPadding}
                            button
                          >
                            <Typography variant="body1" className="flexSpacer">
                              {capitalize(subChild.name)}
                            </Typography>
                            {badge(subitem.badge)}
                          </ListItem>
                        </NavLink>
                      ))}
                    </List>
                  </Collapse>
                </div>
              ) : (
                <NavLink
                  to={`${route.path ? route.path : ""}${
                    subitem.path ? subitem.path : ""
                  }`}
                  exact
                  className={classes.menuLink}
                  activeClassName={classes.menuActive}
                  key={index}
                >
                  <ListItem className={classes.menuSubItem} button>
                    <ListItemIcon>
                      <subitem.icon className={classes.menuIcon} />
                    </ListItemIcon>
                    <Typography variant="body1" className="flexSpacer">
                      {capitalize(subitem.name)}
                    </Typography>
                    {badge(subitem.badge)}
                  </ListItem>
                </NavLink>
              )
            )}
          </List>
        </Collapse>
      </div>
    );
  }

  return (
    <NavLink
      to={route.path}
      exact
      className={classes.menuLink}
      activeClassName={classes.menuActive}
      key={index}
    >
      <ListItem
        className={classes.menuItem}
        button
        onClick={() => toggleMenu(index)}
      >
        <ListItemIcon>
          <route.icon className={classes.menuIcon} />
        </ListItemIcon>
        <Typography variant="body1" className="flexSpacer">
          {capitalize(route.name)}
        </Typography>
        {badge(route.badge)}
      </ListItem>
    </NavLink>
  );
};

SidebarItem.prototypes = {
  route: PropTypes.object,
  index: PropTypes.number,
  activeRoute: PropTypes.number,
  toggleMenu: PropTypes.func,
};

export default SidebarItem;
